import { CModal, CSpinner } from "@coreui/react";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import {
  PartnerRequestBadge,
  usePartnerRequestAlerts,
} from "../../hooks/Partners/usePartnerRequestAlerts";
import { usePartnersQuery } from "../../hooks/Partners/usePartnersQuery";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import NewPartner from "./NewPartner";
import PartnerAccounts from "./PartnerAccounts";
import PartnerRequests from "./PartnerRequests";
import PartnersTable from "./PartnerTable";
import { Chip } from "../../helpers/general";
import { ACCENT, cardStyle } from "../Places/PlacesTable";
import SearchBox from "../../components/ui/SearchBox";

const PAGE_SIZE = 10;

const TABS = [
  { key: 0, label: "Partenaires", hash: "#partners" },
  { key: 1, label: "Demandes de partenariat", hash: "#partner" },
  { key: 2, label: "Comptes partenaires", hash: "#accounts" },
] as const;

const tabFromHash = () =>
  TABS.find((t) => t.hash === window.location.hash)?.key ?? 0;

export default function Partners() {
  const [activeTab, setActiveTab] = useState<number>(tabFromHash);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [addVisible, setAddVisible] = useState(false);

  const {
    total: requestTotal,
    hasNew: hasNewRequests,
    markSeen,
  } = usePartnerRequestAlerts();
  const { Partners, fetchingPartners, deletePartner, mutating } =
    usePartnersQuery();

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  useEffect(() => {
    if (activeTab === 1) markSeen();
  }, [activeTab, markSeen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const changeTab = (key: number) => {
    setActiveTab(key);
    window.history.replaceState(null, "", TABS[key].hash);
  };

  // Filter first, then paginate
  const filtered = useMemo(
    () =>
      (Partners ?? []).filter(
        (p: any) =>
          p?.name?.toLowerCase()?.includes(searchQuery) ||
          p?.link?.toLowerCase()?.includes(searchQuery),
      ),
    [Partners, searchQuery],
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = usePaginatedContent(currentPage, PAGE_SIZE, filtered);

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deletePartner(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = Partners?.length ?? 0;

  return (
    <AdminRoute>
      <section>
        {/* Title + count + add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Partenaires</h5>
            {activeTab === 0 && Partners && (
              <Chip tone="warning">
                {total} {total > 1 ? "Partenaires" : "Partenaire"}
              </Chip>
            )}
          </div>
          {activeTab === 0 && (
            <button
              type="button"
              onClick={() => setAddVisible(true)}
              className="btn btn-primary text-white rounded-pill px-4 border-0"
            >
              + Ajouter un partenaire
            </button>
          )}
        </div>

        {/* Tabs */}
        <div
          className="d-flex gap-2 mb-3 pb-1"
          style={{ overflowX: "auto", whiteSpace: "nowrap" }}
        >
          {TABS.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => changeTab(tab.key)}
                className="border-0 rounded-pill flex-shrink-0 d-flex align-items-center"
                style={{
                  padding: "8px 18px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: active ? "#fff" : "#444",
                  backgroundColor: active ? ACCENT : "#fff",
                  boxShadow: active
                    ? "0 4px 12px rgba(240,129,63,0.35)"
                    : "0 1px 3px rgba(0,0,0,0.08)",
                  transition: "all .15s ease",
                }}
              >
                {tab.label}
                {tab.key === 1 && (
                  <PartnerRequestBadge
                    total={requestTotal}
                    hasNew={hasNewRequests}
                  />
                )}
              </button>
            );
          })}
        </div>

        {activeTab === 0 && (
          <>
            <div
              style={{ ...cardStyle, padding: "12px 16px" }}
              className="d-flex flex-wrap align-items-center gap-2 mb-3"
            >
              <SearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Rechercher un partenaire"
              />
              <button
                type="button"
                disabled={mutating || selectedCount === 0}
                onClick={handleBulkDelete}
                className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
              >
                {mutating ? (
                  <>
                    <CSpinner size="sm" /> {general.fr.wait}
                  </>
                ) : (
                  <>
                    <DeleteIcon fill={colors.primary} /> Supprimer
                    {selectedCount > 0 && ` (${selectedCount})`}
                  </>
                )}
              </button>
            </div>

            <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <RenderTable
                loading={fetchingPartners}
                render={() =>
                  paginated?.length > 0 ? (
                    <PartnersTable
                      Partners={paginated as Partner[]}
                      PartnersToBeDeleted={dispatch}
                    />
                  ) : (
                    <p className="text-center text-muted py-5 mb-0">
                      {general.fr.noData}
                    </p>
                  )
                }
              />
            </div>

            <Pagination
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              siblingCount={1}
              totalCount={totalPages}
              pageSize={PAGE_SIZE}
            />
          </>
        )}

        {activeTab === 1 && <PartnerRequests />}
        {activeTab === 2 && <PartnerAccounts />}

        <CModal
          size="lg"
          alignment="center"
          visible={addVisible}
          onClose={() => setAddVisible(false)}
        >
          <NewPartner
            onAccept={() => {}}
            onClose={() => setAddVisible(false)}
          />
        </CModal>
      </section>
    </AdminRoute>
  );
}
